import React from "react";
import PageHero, { FeatureList } from "@/components/PageHero";
import { CTAFooter } from "@/pages/Prestations";
import { CatalogPackages } from "@/components/CatalogPackages";
import { useSeo, JsonLd } from "@/lib/seo";
import { Mic2, Music, Users, Brain, Check, Headphones, Award, Crown } from "lucide-react";

export default function BlindTestKaraoke() {
  useSeo({
    title: "Blind test & karaoké Nord | My Animation",
    description:
      "Animation blind test, quiz et karaoké pour bars, entreprises, associations, anniversaires et événements privés dans le Nord et les Hauts-de-France. Forfaits dès 290 €.",
    path: "/blind-test-karaoke",
    keywords: "animation blind test Nord, karaoké Lille, blind test entreprise, karaoké bar, animation quiz Hauts-de-France",
  });

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Animation Blind Test, Quiz & Karaoké",
    provider: { "@type": "LocalBusiness", name: "My Animation" },
    areaServed: "Hauts-de-France, Nord, Pas-de-Calais, Belgique",
    description: "Animation blind test, quiz musicaux et karaoké pour bars, entreprises, associations, collectivités, anniversaires et soirées privées.",
  };

  return (
    <>
      <JsonLd data={serviceLd} />
      <PageHero
        eyebrow="Blind Test & Karaoké"
        title={<>Des animations <span className="italic gold-text">qui rassemblent.</span></>}
        subtitle="Animations interactives — blind test, quiz musicaux et karaoké — pour bars, restaurants, entreprises, associations, collectivités, anniversaires et soirées privées dans tout le Nord, le Pas-de-Calais et les Hauts-de-France."
        img="https://images.unsplash.com/photo-1516280440614-37939bbacd81?auto=format&fit=crop&w=1600&q=80"
      />

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="card-luxe p-8 md:p-12 reveal">
            <Brain size={32} strokeWidth={1} className="text-[#DCB457] mb-6" />
            <h2 className="font-serif-luxe text-3xl md:text-4xl">Blind Test & Quiz</h2>
            <p className="text-white/65 mt-4 leading-relaxed">
              Animation interactive pour bars, entreprises, associations, collectivités,
              anniversaires et soirées privées. Plusieurs catégories musicales,
              compétition par équipes ou en solo, et animation rythmée par un présentateur.
            </p>
            <div className="mt-8">
              <FeatureList items={[
                "Catégories au choix (rock, variétés, années 80…)",
                "Quiz musical par équipes",
                "Sonorisation & micro inclus",
                "Animation rythmée et participative",
                "Adaptable à tous types d'événements",
              ]} />
            </div>
          </div>
          <div className="card-luxe p-8 md:p-12 reveal">
            <Mic2 size={32} strokeWidth={1} className="text-[#DCB457] mb-6" />
            <h2 className="font-serif-luxe text-3xl md:text-4xl">Karaoké</h2>
            <p className="text-white/65 mt-4 leading-relaxed">
              Animation participative idéale pour bars, restaurants, campings, soirées
              privées, associations et événements d'entreprise. Bibliothèque musicale
              étendue, sonorisation pro, micros HF et écran intégré.
            </p>
            <div className="mt-8">
              <FeatureList items={[
                "Bibliothèque française & internationale",
                "Micros HF et sonorisation pro",
                "Écran intégré pour les paroles",
                "Animation par un MC sur demande",
                "Format adapté bar, soirée privée ou pro",
              ]} />
            </div>
          </div>
        </div>
      </section>

      <CatalogPackages
        category="blindtest"
        title="Nos forfaits blind test & karaoké"
        subtitle={(n) => (<>{n || "Nos"} formule{n > 1 ? "s" : ""}, <span className="italic gold-text">une ambiance garantie.</span></>)}
        icons={[Headphones, Award, Crown]}
        testidPrefix="btk-pack-"
        featuredBadgeLabel="Le plus choisi"
        ctaLabel="Demander ce forfait"
        footerNote="Tarifs TTC · Devis personnalisé disponible"
        comparisonExtraCols={[
          { header: "Blind test", cell: () => <Check size={18} strokeWidth={1.5} className="text-[#DCB457]" /> },
        ]}
      />

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 bg-[#080808] border-y border-[#DCB457]/15">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-14 reveal">
            <span className="eyebrow">Pour qui ?</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3">Tous publics, <span className="italic gold-text">toutes occasions.</span></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { i: Users, t: "Bars & restaurants", d: "Animation hebdomadaire ou ponctuelle pour fidéliser votre clientèle." },
              { i: Music, t: "Soirées privées", d: "Anniversaires, soirées entre amis, fêtes de famille." },
              { i: Brain, t: "Entreprises", d: "Team building, afterworks, soirées d'entreprise rythmées." },
              { i: Mic2, t: "Associations & collectivités", d: "Soirée associative, kermesse, événement local." },
            ].map(({ i: Icon, t, d }) => (
              <div key={t} className="card-luxe p-7 reveal">
                <Icon size={28} strokeWidth={1} className="text-[#DCB457] mb-5" />
                <h3 className="font-serif-luxe text-2xl">{t}</h3>
                <p className="text-white/55 mt-2 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTAFooter />
    </>
  );
}

